import Avatar from 'components/Avatar';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import InvoiceCost from 'components/invoices/InvoiceCost';
import InvoiceIntegrationStatus from 'components/invoices/InvoiceIntegrationStatus';
import InvoiceLandingPlaceholder from 'components/invoices/InvoiceLandingPlaceholder';
import { TableWidgetProjectBlock } from 'components/superreports/widgets/table/blocks/TableWidgetProjectBlock';
import {
  TableRowPlaceholder,
  TdPlaceholder,
} from 'components/superreports/widgets/table/TableRowPlaceholder';
import Tooltip from 'components/Tooltip';
import { SortableTh, Table, Tbody, Td, TdActions, Th, Thead, Tr } from 'components/ui-blocks/table';
import type { FactoryApp } from 'factories/apps';
import { formatInvoiceRange, getInvoiceOptions } from 'factories/invoices';
import { useIsListEmpty } from 'hooks/useIsListEmpty';
import { DateTime } from 'luxon';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import { useDispatch } from 'react-redux';
import { DISALLOWED_CHARACTERS } from 'redux/modules/clients.sync';
import { deleteInvoiceById } from 'redux/modules/invoice.actions';
import { initialState } from 'redux/modules/invoice.reducer';
import type { InvoiceQuery } from 'redux/modules/invoice.types';
import { InvoiceListFields, setSortBy, toggleCurrentInvoiceId } from 'redux/modules/invoice.types';
import type { UserPermissionsFacade } from 'redux/modules/user.types';
import type { Currency } from 'types/account';
import type { AppAccount } from 'types/apps';
import type { InvoiceDraft, RawInvoice } from 'types/invoice';
import { InvoiceStatus as Status } from 'types/invoice';
import type { ThunkDispatch } from 'types/redux';
import { checkStringForPattern } from 'utils/string';
import InvoiceExportButton from './InvoiceExportButton';
import styles from './InvoiceList.module.scss';
import InvoiceStatus from './InvoiceStatus';

interface Props {
  invoices: Array<RawInvoice | InvoiceDraft>;
  isLoading: boolean;
  sortBy: {
    field: InvoiceListFields;
    direction?: 'asc' | 'desc';
  };
  apps: Array<FactoryApp>;
  appAccounts: Record<number, Array<AppAccount>>;
  appEntities: Record<number, FactoryApp>;
  permissions: UserPermissionsFacade;
  query: InvoiceQuery;
  currencyLookup: Record<string, Currency>;
}

const InvoiceList: React.FC<Props> = ({
  invoices,
  isLoading,
  sortBy,
  apps,
  appAccounts,
  appEntities,
  permissions,
  query,
  currencyLookup,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { isEmpty } = useIsListEmpty(invoices.length, isLoading);

  function renderRow(invoice: RawInvoice | InvoiceDraft) {
    function handleActionClick(option: DropdownOption) {
      if (option.action === 'delete') {
        dispatch(deleteInvoiceById(option.idRef!));
      }
      if (option.action === 'view') {
        dispatch(toggleCurrentInvoiceId({ id: option.idRef! }));
      }
    }

    // todo backend will be changed from array to object at some point
    const externalReference = invoice?.external_references?.[0];
    const syncedApp =
      externalReference != null
        ? apps.find(app => app.provider === externalReference?.provider_type)
        : null;
    const isAwaitingCreation = invoice?.status === Status.Incomplete;
    const isAwaitingSync = 'isAwaitingSync' in invoice && invoice.isAwaitingSync;
    const invoiceUrl = externalReference ? externalReference.url : null;

    const { invoiceListOptions, exportOptions } = getInvoiceOptions(
      invoice,
      appEntities,
      appAccounts
    );

    const dropdownOptions: Array<DropdownOption> = [
      {
        idRef: invoice.id,
        action: 'view',
        name: 'View invoice',
        faIcon: 'file',
      },
    ];

    if (permissions.canDelete.invoices && !invoice.deleted) {
      dropdownOptions.push({
        idRef: invoice.id,
        action: 'delete',
        name: 'Void invoice',
        faIcon: 'archive',
        confirm: true,
      });
    }

    function renderSyncStatus() {
      if (isAwaitingCreation || isAwaitingSync) {
        return <TdPlaceholder />;
      }

      if (invoice.external_references?.length === 0 && invoiceListOptions.length > 1) {
        return (
          <InvoiceExportButton
            invoice={invoice}
            options={invoiceListOptions}
            optionsCount={exportOptions.length}
            buttonSize="small"
          />
        );
      } else {
        return (
          <>
            <DiagnosticResourceId resource="external invoice id" id={externalReference?.id} />
            <InvoiceIntegrationStatus
              paid={invoice.paid}
              invoiceUrl={invoiceUrl}
              synced={!!externalReference}
              logoUrl={syncedApp?.logoUrl}
              logoAlt={syncedApp?.displayName}
            />
          </>
        );
      }
    }

    const renderProject = () => {
      if (isAwaitingCreation) {
        return <TdPlaceholder />;
      }
      if (!invoice.project) {
        return '—';
      }
      const maybeHasInvalidClientName = checkStringForPattern(
        invoice.project.client.name,
        DISALLOWED_CHARACTERS
      );

      return (
        <>
          <TableWidgetProjectBlock project={invoice.project} />
          {maybeHasInvalidClientName ? (
            <Tooltip
              className={styles.nameWarning}
              title="Your client’s name contains special characters that we may need to remove to sync this invoice."
            >
              <FontAwesome className={styles.nameWarningBadge} name="exclamation-triangle" />
            </Tooltip>
          ) : null}
        </>
      );
    };

    return (
      <Tr key={invoice.id}>
        <Td className={styles.tdContainer}>{renderProject()}</Td>
        <Td className={styles.tdContainer}>{renderSyncStatus()}</Td>
        <Td className={styles.tdContainer}>
          <DiagnosticResourceId resource="invoice" id={invoice.id} />
          {isAwaitingCreation ? <TdPlaceholder /> : <InvoiceStatus status={invoice.status} />}
        </Td>
        <Td className={styles.tdContainer}>
          {isAwaitingCreation ? (
            <TdPlaceholder />
          ) : (
            <InvoiceCost
              totalCost={invoice.total_cost}
              externalCost={invoice.external_total_cost}
              currencyLookup={currencyLookup}
            />
          )}
        </Td>
        <Td className={styles.tdContainer}>
          {isAwaitingCreation ? (
            <TdPlaceholder />
          ) : (
            <>
              <Avatar
                shape="circle"
                src={invoice.user.avatar.medium_retina}
                name={invoice.user.name}
                fallbackToInitials
                className="fs-exclude"
              />
              <span style={{ marginLeft: 5 }} className="fs-mask">
                <DiagnosticResourceId resource="user" id={invoice.user.id} />
                {invoice.user.name}
              </span>
            </>
          )}
        </Td>
        <Td className={styles.tdContainer}>
          {DateTime.fromISO(invoice.updated_at).toLocaleString({
            ...DateTime.DATETIME_MED,
            year: undefined,
          })}
        </Td>
        <Td className={styles.tdContainer}>
          {invoice.due_date
            ? DateTime.fromISO(invoice.due_date).toLocaleString({
                ...DateTime.DATE_MED,
                year: undefined,
              })
            : '—'}
        </Td>
        <Td className={styles.tdContainer}>{formatInvoiceRange(invoice.from, invoice.to)}</Td>
        <TdActions<DropdownOption> onClick={handleActionClick} options={dropdownOptions} />
      </Tr>
    );
  }

  function handleSortBy({
    field,
    direction,
  }: {
    field: InvoiceListFields;
    direction: 'asc' | 'desc';
  }) {
    dispatch(setSortBy({ field, direction }));
  }

  function renderHeader() {
    return (
      <Tr>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.ClientName}
          field={InvoiceListFields.ClientName}
          direction={sortBy.direction}
        >
          Client
        </SortableTh>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.Integration}
          field={InvoiceListFields.Integration}
          direction={sortBy.direction}
        >
          Integration
        </SortableTh>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.Status}
          field={InvoiceListFields.Status}
          direction={sortBy.direction}
        >
          Status
        </SortableTh>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.TotalCost}
          field={InvoiceListFields.TotalCost}
          direction={sortBy.direction}
        >
          Total
        </SortableTh>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.User}
          field={InvoiceListFields.User}
          direction={sortBy.direction}
        >
          Created by
        </SortableTh>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.UpdatedAt}
          field={InvoiceListFields.UpdatedAt}
          direction={sortBy.direction}
        >
          Updated at
        </SortableTh>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.DueDate}
          field={InvoiceListFields.DueDate}
          direction={sortBy.direction}
        >
          Due date
        </SortableTh>
        <SortableTh
          onClick={handleSortBy}
          isActive={sortBy.field === InvoiceListFields.DateRange}
          field={InvoiceListFields.DateRange}
          direction={sortBy.direction}
        >
          Date range
        </SortableTh>
        <Th actions />
      </Tr>
    );
  }

  function renderBody() {
    if (isLoading && invoices.length === 0) {
      return <TableRowPlaceholder rowCount={20} colCount={7} placeHolder={<TdPlaceholder />} />;
    }
    return invoices.map(renderRow);
  }

  if (isEmpty) {
    return (
      <InvoiceLandingPlaceholder
        query={query}
        initialQuery={initialState.query}
        isLoading={false}
        isEmpty={isEmpty}
      />
    );
  }

  return (
    <Table>
      <Thead>{renderHeader()}</Thead>
      <Tbody>{renderBody()}</Tbody>
    </Table>
  );
};

export default InvoiceList;
